import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { saveBlobAsFile } from '../../../api/apiUtils.js'
import {
  downloadComputeExcel,
  getComputeData,
  getTotalSum,
} from '../../../api/compute.js'
import DownloadIcon from '../../../assets/icons/DownloadIcon'
import SearchIconBtn from '../../../assets/icons/SearchIconBtn.jsx'
import Search from '../../../components/common/Search.jsx'
import { columns, searchFields } from '../../../constants/computeColumn.js'
import { GLOBAL_CONSTANTS } from '../../../constants/globalConstants'
import { DEFAULT_PAGE, getValidCriteria } from '../../../constants/index.js'
import { useSnackbar } from '../../../contexts/SnackbarProvider'
import { fetchEntityListIfNeeded } from '../../../store/actions/entityTanActions.js'
import { ROLE_CAPABILITIES } from '../../../utils/role-capabilities.js'
import CommonTable from '../../common/CommonTable'
import KeyFilter from '../Define/common/KeyFilter.jsx'

const Compute = () => {
  const { showSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const roleTypes = useSelector(state => state.user.role)
  const roleConfig = ROLE_CAPABILITIES[roleTypes] || ROLE_CAPABILITIES.default
  const [tableData, setTableData] = useState([])
  const [showTable, setShowTable] = useState(false)
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const [totalSumData, setTotalSumData] = useState({})

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])
  const [defaultSearchCOList, setDefaultSearchCOList] = useState([])

  useEffect(() => {
    const payload = {
      pageFilter: {
        currentPage: 1,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'entityName',
        sortOrder: 'ASC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
    }
    if (roleConfig.canCreate) {
      dispatch(fetchEntityListIfNeeded(payload))
    }
  }, [dispatch, roleConfig.canCreate])

  const fetchComputeData = async ({
    page = 1,
    pageSize = itemsPerPage,
    searchList = [],
    defaultSearch = defaultSearchCOList,
    tabStatus,
    columns,
  } = {}) => {
    const { tan, periodFilters } = defaultSearch || {}
    setLoading(true)

    try {
      const payload = {
        pageFilter: {
          currentPage: page,
          includeCount: true,
          pageSize: pageSize,
          sortField: 'finalReturnTdsSection',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: searchList,
        entityTANs: tan ? tan.split(',') : [],
        periodFilters,
      }

      // Hit both APIs in parallel
      const [computeResponse, totalSumResponse] = await Promise.all([
        getComputeData(payload),
        getTotalSum(payload),
      ])

      const tableData = computeResponse?.data?.data?.content || []
      const totalSumData = totalSumResponse?.data?.data || null
      setTableData(tableData)
      setTotalItems(computeResponse?.data?.data?.totalElements || 0)
      setTotalSumData(totalSumData)
    } catch (error) {
      console.error('Error fetching data:')
    } finally {
      setLoading(false)
    }
  }

  const handleViewClick = async data => {
    setShowTable(true)
    setDefaultSearchCOList(data)
    await fetchComputeData({
      page: 1,
      pageSize: itemsPerPage,
      defaultSearch: data,
    })
  }

  const handlePageChange = async page => {
    setCurrentPage(page)
    await fetchComputeData({
      page,
      pageSize: itemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      defaultSearch: defaultSearchCOList,
    })
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    await fetchComputeData({
      page: 1,
      pageSize: newItemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      defaultSearch: defaultSearchCOList,
    })
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await await fetchComputeData({
      page: 1,
      pageSize: itemsPerPage,
      searchList,
      defaultSearch: defaultSearchCOList,
    })

    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await await fetchComputeData({
      page: 1,
      pageSize: itemsPerPage,
      searchList: [],
      defaultSearch: defaultSearchCOList,
    })

    setSearchOpen(false)
  }

  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    setStartDownloading(true)
    const { tan, periodFilters } = defaultSearchCOList || {}
    const payload = {
      pageFilter: {
        currentPage: currentPage,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'finalReturnTdsSection',
        sortOrder: 'DESC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
      entityTANs: tan ? tan.split(',') : [],
      periodFilters,
    }
    const result = await downloadComputeExcel(payload)
    if (result?.success && result?.data) {
      saveBlobAsFile(result.data, 'Compute.xlsx')
      setStartDownloading(false)
    } else if (result?.error) {
      showSnackbar(result.error, 'error')
      setStartDownloading(false)
    }
  }

  return (
    <>
      {/* key filters */}
      <KeyFilter
        handleViewClick={handleViewClick}
        isYear={true}
        isMonth={true}
      />

      {/* subheader */}
      <Box
        sx={{
          display: showTable ? 'flex' : 'none',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          Compute
        </Typography>

        {/* Right side - Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <IconButton onClick={handleDownload} disabled={startDownloading}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton onClick={handleSearch}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}

      {showTable && (
        <Box>
          <CommonTable
            columns={columns}
            data={tableData}
            loading={loading}
            onEdit={{}}
            onDelete={{}}
            emptyMessage="No data found"
            showFooter={true}
            showActions={false}
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalSumData={totalSumData}
            tabStatus={'compute'}
          />
        </Box>
      )}

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </>
  )
}

export default Compute
